import React from "react";

type VideoPlaybackContextType = {
  isMuted: boolean;
  getSavedProgress: (activityUid: string) => number;
  saveProgress: (activityUid: string, currentTime: number) => void;
  clearSavedProgress: (activityUid: string) => void;
  setMuted: (muted: boolean) => void;
};

type VideoPlaybackActivationHandlers = {
  isVideoActive: boolean;
  activateVideo: () => void;
  deactivateVideo: () => void;
};

const DEFAULT_PROGRESS_SECONDS = 0;

const VideoPlaybackContext = React.createContext<VideoPlaybackContextType>({
  isMuted: true,
  getSavedProgress: () => DEFAULT_PROGRESS_SECONDS,
  saveProgress: () => {},
  clearSavedProgress: () => {},
  setMuted: () => {},
});

/**
 * Stores search-card video playback state that should persist across cards,
 * such as mute preference and resumable playback progress.
 */
export function VideoPlaybackProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isMuted, setIsMuted] = React.useState(true);
  const savedProgressRef = React.useRef<Record<string, number>>({});

  const getSavedProgress = React.useCallback((activityUid: string) => {
    return savedProgressRef.current[activityUid] ?? DEFAULT_PROGRESS_SECONDS;
  }, []);

  const saveProgress = React.useCallback(
    (activityUid: string, currentTime: number) => {
      savedProgressRef.current[activityUid] = currentTime;
    },
    [],
  );

  const clearSavedProgress = React.useCallback((activityUid: string) => {
    delete savedProgressRef.current[activityUid];
  }, []);

  const value = React.useMemo(
    () => ({
      isMuted,
      getSavedProgress,
      saveProgress,
      clearSavedProgress,
      setMuted: setIsMuted,
    }),
    [isMuted, getSavedProgress, saveProgress, clearSavedProgress],
  );

  return (
    <VideoPlaybackContext.Provider value={value}>
      {children}
    </VideoPlaybackContext.Provider>
  );
}

/** Reads the shared search card video playback state. */
export function useVideoPlayback() {
  return React.useContext(VideoPlaybackContext);
}

/**
 * Builds the focus and hover handlers used to toggle a desktop listing card's
 * preview video.
 */
export function useVideoPlaybackActivation(
  shouldEnableVideo: boolean | undefined,
): VideoPlaybackActivationHandlers {
  const [isVideoActive, setIsVideoActive] = React.useState(false);

  const activateVideo = React.useCallback(() => {
    if (shouldEnableVideo) {
      setIsVideoActive(true);
    }
  }, [shouldEnableVideo]);

  const deactivateVideo = React.useCallback(() => {
    setIsVideoActive(false);
  }, []);

  return React.useMemo(
    () => ({
      isVideoActive,
      activateVideo,
      deactivateVideo,
    }),
    [activateVideo, deactivateVideo, isVideoActive],
  );
}
